import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPrinter, FiCheck, FiX, FiClock, FiPackage } = FiIcons;

function OrderManagement() {
  const { user, orders, updateOrderStatus } = useApp();
  const [filter, setFilter] = useState('all');
  
  const prisonOrders = orders.filter(order => order.prisonId === user.prisonId);
  const filteredOrders = filter === 'all' 
    ? prisonOrders 
    : prisonOrders.filter(order => order.status === filter);

  const handleStatusUpdate = (orderId, newStatus) => {
    updateOrderStatus(orderId, newStatus);
  };

  const handlePrint = (order) => {
    // In a real application, this would generate a print-friendly receipt
    const printContent = `
      ORDER RECEIPT
      =============
      Order ID: ${order.id}
      Customer: ${order.customerName}
      Date: ${new Date(order.createdAt).toLocaleDateString()}
      
      ITEMS:
      ${order.items.map(item => `${item.name} x${item.quantity} - £${item.total.toFixed(2)}`).join('\n')}
      
      TOTAL: £${order.total.toFixed(2)}
      STATUS: ${order.status.toUpperCase()}
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head><title>Order Receipt</title></head>
        <body>
          <pre style="font-family: monospace; font-size: 14px;">
            ${printContent}
          </pre>
          <script>window.print(); window.close();</script>
        </body>
      </html>
    `);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-government-600 bg-government-100';
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-government-900 mb-4">
          Order Management
        </h1>
        
        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All Orders' },
            { key: 'pending', label: 'Pending' },
            { key: 'completed', label: 'Completed' },
            { key: 'cancelled', label: 'Cancelled' }
          ].map((filterOption) => (
            <button
              key={filterOption.key}
              onClick={() => setFilter(filterOption.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === filterOption.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-government-600 hover:bg-government-50'
              }`}
            >
              {filterOption.label}
            </button>
          ))}
        </div>
      </motion.div>

      {filteredOrders.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 text-center"
        >
          <SafeIcon icon={FiPackage} className="w-16 h-16 text-government-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-government-900 mb-2">
            No orders found
          </h3>
          <p className="text-government-600">
            {filter === 'all' ? 'No orders have been placed yet' : `No ${filter} orders`}
          </p>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {filteredOrders.map((order, index) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-government-900">
                    Order #{order.id.substring(0, 8)}
                  </h3>
                  <p className="text-government-600">
                    Customer: {order.customerName}
                  </p>
                  <p className="text-sm text-government-500">
                    {new Date(order.createdAt).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                  <span className="text-xl font-bold text-primary-600">
                    £{order.total.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-4">
                <h4 className="font-medium text-government-900 mb-2">Items:</h4>
                <div className="space-y-2">
                  {order.items.map((item) => (
                    <div key={item.cartId} className="flex justify-between items-center py-2 border-b border-government-100">
                      <div className="flex items-center space-x-3">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-8 h-8 object-cover rounded"
                        />
                        <span className="text-government-900">{item.name}</span>
                        <span className="text-government-600">x{item.quantity}</span>
                      </div>
                      <span className="font-medium">£{item.total.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handlePrint(order)}
                  className="flex items-center space-x-2 px-4 py-2 bg-government-600 hover:bg-government-700 text-white rounded-lg font-medium transition-colors"
                >
                  <SafeIcon icon={FiPrinter} className="w-4 h-4" />
                  <span>Print</span>
                </button>
                
                {order.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate(order.id, 'completed')}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                    >
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      <span>Complete</span>
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(order.id, 'cancelled')}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrderManagement;